import { useState, useEffect } from 'react';
import { History, Eye, Trash2, CheckCircle2, Archive, X } from 'lucide-react';
import { toast } from 'sonner';
import { api } from '../../lib/api';
import Button from '../ui/Button';
import Card from '../ui/Card';
import Spinner from '../ui/Spinner';

interface AnalysisHistoryItem {
  analysisId: string;
  createdAt: string;
  isActive: boolean;
  archivedAt?: string;
  summary: string;
  completenessScore?: number;
}

interface AnalysisHistoryProps {
  caseId: string;
  onViewAnalysis: (analysisId: string) => void;
  onClose: () => void;
}

export default function AnalysisHistory({  caseId, onViewAnalysis, onClose }: AnalysisHistoryProps) {
  const [history, setHistory] = useState<AnalysisHistoryItem[]>([]);
  const [activeAnalysisId, setActiveAnalysisId] = useState<string | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [isDeleting, setIsDeleting] = useState<string | null>(null);

  // Load history when component mounts
  useEffect(() => {
    loadHistory();
  }, []); // Empty dependency array means run once on mount

  const loadHistory = async () => {
    setIsLoading(true);
    try {
      const response = await api.get(`/data-requirements/cases/${ caseId}/data-requirements/history`);
      setHistory(response.analyses || []);
      setActiveAnalysisId(response.activeAnalysisId || null);
    } catch (error: any) {
      console.error('Error loading analysis history:', error);
      toast.error('Failed to load analysis history');
    } finally {
      setIsLoading(false);
    }
  };

  const handleDeleteArchived = async (analysisId: string) => {
    const analysis = history.find(a => a.analysisId === analysisId);

    if (!analysis) return;

    if (analysis.isActive) {
      toast.error('Cannot delete active analysis');
      return;
    }

    if (!confirm(`Are you sure you want to permanently delete this archived analysis?\n\nCreated: ${formatDate(analysis.createdAt)}\nScore: ${analysis.completenessScore || 'N/A'}%`)) {
      return;
    }

    setIsDeleting(analysisId);
    try {
      await api.delete(`/data-requirements/cases/${ caseId}/data-requirements/${analysisId}`);

      // Remove from local state
      setHistory(history.filter(a => a.analysisId !== analysisId));

      toast.success('Archived analysis deleted successfully');
    } catch (error: any) {
      console.error('Error deleting analysis:', error);
      const errorMessage = error?.response?.data?.detail || 'Failed to delete archived analysis';
      toast.error(errorMessage);
    } finally {
      setIsDeleting(null);
    }
  };

  const handleViewAnalysis = (analysisId: string) => {
    onViewAnalysis(analysisId);
    onClose();
  };

  const formatDate = (dateString: string) => {
    try {
      const date = new Date(dateString);
      return date.toLocaleString('en-US', {
        month: 'short',
        day: 'numeric',
        year: 'numeric',
        hour: '2-digit',
        minute: '2-digit'
      });
    } catch {
      return dateString;
    }
  };

  const getTimeSince = (dateString: string) => {
    try {
      const date = new Date(dateString);
      const now = new Date();
      const diffMs = now.getTime() - date.getTime();
      const diffMins = Math.floor(diffMs / 60000);
      const diffHours = Math.floor(diffMs / 3600000);
      const diffDays = Math.floor(diffMs / 86400000);

      if (diffMins < 60) return `${diffMins}m ago`;
      if (diffHours < 24) return `${diffHours}h ago`;
      return `${diffDays}d ago`;
    } catch {
      return '';
    }
  };

  if (isLoading) {
    return (
      <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50">
        <Card className="w-full max-w-3xl max-h-[80vh] p-6 m-4">
          <div className="flex flex-col items-center justify-center py-8">
            <Spinner size="lg" />
            <p className="mt-4 text-neutral-600">Loading analysis history...</p>
          </div>
        </Card>
      </div>
    );
  }

  return (
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50">
      <Card className="w-full max-w-3xl max-h-[80vh] flex flex-col p-0 m-4">
        {/* Header */}
        <div className="flex items-center justify-between p-6 border-b border-neutral-200">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 rounded-full bg-primary-100 flex items-center justify-center">
              <History className="w-5 h-5 text-primary-600" />
            </div>
            <div>
              <h2 className="text-xl font-bold text-neutral-900">Analysis History</h2>
              <p className="text-sm text-neutral-600">
                {history.length} {history.length === 1 ? 'analysis' : 'analyses'} • {history.filter(a => a.isActive).length} active
              </p>
            </div>
          </div>
          <Button variant="ghost" size="sm" onClick={onClose}>
            <X className="w-5 h-5" />
          </Button>
        </div>

        {/* Content */}
        <div className="flex-1 overflow-y-auto p-6">
          {history.length === 0 ? (
            <div className="text-center py-12">
              <Archive className="w-16 h-16 text-neutral-300 mx-auto mb-4" />
              <p className="text-neutral-600">No analysis history found</p>
            </div>
          ) : (
            <div className="space-y-3">
              {history.map((item) => (
                <Card
                  key={item.analysisId}
                  className={`p-4 transition-all ${
                    item.isActive
                      ? 'border-l-4 border-l-green-500 bg-green-50'
                      : 'border-l-4 border-l-neutral-300'
                  }`}
                >
                  <div className="flex items-start justify-between gap-4">
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-2 mb-2">
                        {item.isActive ? (
                          <div className="flex items-center gap-1.5 text-green-700">
                            <CheckCircle2 className="w-4 h-4" />
                            <span className="text-xs font-semibold uppercase">Active</span>
                          </div>
                        ) : (
                          <div className="flex items-center gap-1.5 text-neutral-500">
                            <Archive className="w-4 h-4" />
                            <span className="text-xs font-semibold uppercase">Archived</span>
                          </div>
                        )}
                        {item.completenessScore !== undefined && (
                          <span className="text-xs px-2 py-0.5 bg-primary-100 text-primary-700 rounded-full font-medium">
                            {item.completenessScore}% complete
                          </span>
                        )}
                      </div>

                      <p className="text-sm text-neutral-700 mb-2 line-clamp-2">
                        {item.summary || 'No summary available'}
                      </p>

                      <div className="flex items-center gap-4 text-xs text-neutral-500">
                        <span className="flex items-center gap-1">
                          Created: {formatDate(item.createdAt)}
                        </span>
                        <span className="text-neutral-400">•</span>
                        <span>{getTimeSince(item.createdAt)}</span>
                        {item.archivedAt && (
                          <>
                            <span className="text-neutral-400">•</span>
                            <span className="text-amber-600">
                              Archived: {formatDate(item.archivedAt)}
                            </span>
                          </>
                        )}
                      </div>

                      <div className="mt-2 text-xs text-neutral-400 font-mono">
                        ID: {item.analysisId}
                      </div>
                    </div>

                    <div className="flex items-center gap-2 flex-shrink-0">
                      <Button
                        variant="outline"
                        size="sm"
                        onClick={() => handleViewAnalysis(item.analysisId)}
                        title="View this analysis"
                      >
                        <Eye className="w-4 h-4" />
                      </Button>
                      {!item.isActive && (
                        <Button
                          variant="ghost"
                          size="sm"
                          onClick={() => handleDeleteArchived(item.analysisId)}
                          disabled={isDeleting === item.analysisId}
                          loading={isDeleting === item.analysisId}
                          title="Delete archived analysis"
                        >
                          <Trash2 className="w-4 h-4 text-red-600" />
                        </Button>
                      )}
                    </div>
                  </div>
                </Card>
              ))}
            </div>
          )}
        </div>

        {/* Footer */}
        <div className="p-4 border-t border-neutral-200 bg-neutral-50">
          <div className="flex items-center justify-between text-xs text-neutral-600">
            <div className="flex items-center gap-4">
              <span className="flex items-center gap-1.5">
                <CheckCircle2 className="w-3.5 h-3.5 text-green-600" />
                Active analysis shown by default
              </span>
              <span className="flex items-center gap-1.5">
                <Archive className="w-3.5 h-3.5 text-neutral-500" />
                Archived analyses preserved
              </span>
            </div>
            <Button variant="outline" size="sm" onClick={onClose}>
              Close
            </Button>
          </div>
        </div>
      </Card>
    </div>
  );
}
